/**
 * A single-slot async mutex.
 *
 * Small enough to own rather than depend on. It exists for exactly one job:
 * making concurrent 401s share one token refresh instead of racing. See
 * src/api/baseQuery.ts.
 *
 * Two queues, not one. An acquirer is handed the lock still held; a waiter is
 * only told the lock went free. Sharing a queue between them means a release
 * can hand the lock to something that never releases it, and every later
 * acquire blocks forever.
 */
export class Mutex {
    private acquirers: Array<() => void> = [];
    private waiters: Array<() => void> = [];
    private locked = false;

    isLocked(): boolean {
        return this.locked;
    }

    /** Takes the lock, resolving to the function that releases it. */
    acquire(): Promise<() => void> {
        if (!this.locked) {
            this.locked = true;
            return Promise.resolve(this.releaser());
        }
        return new Promise<() => void>((resolve) => {
            this.acquirers.push(() => resolve(this.releaser()));
        });
    }

    /** Resolves once the lock is free. Does not take it. */
    waitForUnlock(): Promise<void> {
        if (!this.locked) return Promise.resolve();
        return new Promise<void>((resolve) => {
            this.waiters.push(resolve);
        });
    }

    /**
     * Returns a release function that is safe to call more than once: a caller
     * releasing in a `finally` after an early return would otherwise hand the
     * lock out twice.
     */
    private releaser(): () => void {
        let spent = false;
        return () => {
            if (spent) return;
            spent = true;
            this.release();
        };
    }

    private release(): void {
        const next = this.acquirers.shift();
        if (next) {
            // The lock passes straight to the next acquirer; it is never
            // observably free, so waiters are not woken.
            next();
            return;
        }
        this.locked = false;
        const waiting = this.waiters;
        this.waiters = [];
        waiting.forEach((resolve) => resolve());
    }
}

export default Mutex;
